import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document

/**
 * Centered cyan-link footer used on SPA marketing sub-pages (Team, About,
 * Security, Terms, Privacy, Contact, Guide). Visual style mirrors the
 * static trust.html and server-rendered /privacy and /support footers so
 * navigation feels uniform regardless of which back-end actually rendered
 * the page.
 *
 * Internal links (about, team, security, privacy, terms) call back through
 * `onNavigate` so the SPA routes without a full reload + history pushState.
 * External / non-SPA links (Home `/?home`, Blog, Trust, Support, socials)
 * stay as plain anchors and do real navigation.
 */
object MarketingFooter {
    private val isFrench get() = LocaleManager.getCurrentLocale().code == "fr"

    fun html(currentPage: String = ""): String {
        val border = "#2a2a2e"
        val mutedColor = "#666"
        val activeColor = "#ffffff"
        val linkColor = "#00d9ff"
        val copyText = if (isFrench) "Tous droits réservés." else "All rights reserved."

        fun internalLink(id: String, label: String, page: String): String {
            val color = if (currentPage == page) activeColor else linkColor
            val weight = if (currentPage == page) "600" else "400"
            return """<a id="$id" href="/$page" style="color: $color; margin: 0 12px; text-decoration: none; font-weight: $weight; white-space: nowrap;">$label</a>"""
        }
        fun externalLink(label: String, href: String, rel: String = ""): String {
            val relAttr = if (rel.isNotEmpty()) """ rel="$rel"""" else ""
            return """<a href="$href"$relAttr style="color: $linkColor; margin: 0 12px; text-decoration: none; white-space: nowrap;">$label</a>"""
        }

        val homeLabel = if (isFrench) "Accueil" else "Home"
        val blogLabel = "Blog"
        val trustLabel = if (isFrench) "Confiance" else "Trust"
        val supportLabel = if (isFrench) "Soutien" else "Support"
        val teamLabel = if (isFrench) "Notre équipe" else "Team"

        return """
            <footer style="margin-top: 64px; padding: 32px 24px; border-top: 1px solid $border; color: $mutedColor; font-size: 14px; text-align: center; line-height: 2;">
                ${externalLink(homeLabel, "/?home")}
                ${internalLink("mf-about", StringKey.FOOTER_ABOUT.tr(), "about")}
                ${internalLink("mf-team", teamLabel, "team")}
                ${internalLink("mf-security", StringKey.FOOTER_SECURITY.tr(), "security")}
                ${externalLink(trustLabel, "/trust")}
                ${externalLink(blogLabel, "/blog")}
                ${internalLink("mf-privacy", StringKey.FOOTER_PRIVACY.tr(), "privacy")}
                ${internalLink("mf-terms", StringKey.FOOTER_TERMS.tr(), "terms")}
                ${externalLink(supportLabel, "/support")}
                ${externalLink("LinkedIn", "https://www.linkedin.com/company/wiz3r", "me noopener")}
                ${externalLink("GitHub", "https://github.com/wiz3rapp", "me noopener")}
                ${externalLink("X", "https://x.com/wiz3rapp", "me noopener")}
                ${externalLink("Instagram", "https://www.instagram.com/wiz3rapp/", "me noopener")}
                ${externalLink("Facebook", "https://www.facebook.com/profile.php?id=61575402881454", "me noopener")}
                <div style="color: $mutedColor; font-size: 13px; margin-top: 12px;">© 2026 wiz≡r. $copyText</div>
            </footer>
        """.trimIndent()
    }

    /**
     * Wires the internal-link click handlers to the SPA's onNavigate callback,
     * so e.g. clicking "About" inside /team routes to Screen.About without a
     * full page reload.
     */
    fun attachEventListeners(onNavigate: (String) -> Unit) {
        listOf("about", "team", "security", "privacy", "terms").forEach { page ->
            document.getElementById("mf-$page")?.addEventListener("click", { e ->
                e.preventDefault()
                onNavigate(page)
            })
        }
    }
}
